import { CompanyType } from '@@types/api.types';
import { canPersonaManageOrgTeam } from '@azymmetric/shared/access/intelligence-persona.types';
import { MainLayout } from '@components/layouts/main.layout';
import { TechnologyTechnologyModal } from '@components/pages/technology/components/technology-technology-modal.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { ReferralTile } from '@components/shared/referral-tile.component';
import { useGetCurrentUser, useGetUserSelectedCompanyId } from '@queries/users.query';
import { routes } from '@routes';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { CompanyProfileModal } from './components/company-company-profile-modal.component';
import { CompanyDetailsTile } from './components/company-info-card.component';
import { CompanyTeamDetailsTile } from './components/company-manage-team.component';
import { IntelTeamDetailsTile } from './components/intel-team-tile.component';

export const CompanyPage: FunctionComponent = () => {
  const { data: user, isPending } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  const navigate = useNavigate();

  const [isCompanyProfileModalOpen, setIsCompanyProfileModalOpen] = useState(false);
  const [isTechnologyModalOpen, setIsTechnologyModalOpen] = useState(false);

  const userHasValidAccess = Boolean(user?.hasValidAccess);
  const hasCompany = user?.companies && user.companies.length > 0;

  useEffect(() => {
    if (!isPending && !user?.profile) {
      navigate(routes.home.path);
    }
  }, [isPending, navigate, user]);

  const company = user?.companies?.find((c) => c.id === selectedCompanyId) || user?.companies?.[0];

  const isIntelligenceOrganization = company?.type === CompanyType.INTELLIGENCE;
  const canManageIntelOrganization =
    isIntelligenceOrganization && canPersonaManageOrgTeam(user?.selectedCompanyPersona);
  const canManageOrganization = isIntelligenceOrganization ? canManageIntelOrganization : true;
  const canViewIntelOrganizationMembers = isIntelligenceOrganization && Boolean(user?.selectedCompanyPersona);

  return (
    <PageviewTracker>
      <HelmetWrapper title="Company" />
      <MainLayout
        aside={
          <div className={cn('flex flex-col gap-4', (!userHasValidAccess || !hasCompany) && 'hidden')}>
            {company &&
              (isIntelligenceOrganization
                ? canViewIntelOrganizationMembers && <IntelTeamDetailsTile />
                : canManageOrganization && <CompanyTeamDetailsTile company={company} />)}
            {canManageOrganization && <ReferralTile />}
          </div>
        }
        asideClassName="@min-[64rem]/main:max-w-[38%] @min-[80rem]/main:max-w-[30%]"
        columnsStartAtContainer="64rem"
        contentClassName="@min-[64rem]/main:max-w-[62%] @min-[80rem]/main:max-w-[70%]"
      >
        <CompanyDetailsTile
          action={canManageOrganization ? 'edit' : 'none'}
          company={company}
        />
        <CompanyProfileModal
          company={company}
          isOpen={isCompanyProfileModalOpen}
          onCompanyCreated={(id: string) => {}}
          onClose={() => setIsCompanyProfileModalOpen(false)}
          onContinue={() => setIsTechnologyModalOpen(true)}
        />
        <TechnologyTechnologyModal
          isEdit={false}
          isOpen={isTechnologyModalOpen}
          onClose={() => setIsTechnologyModalOpen(false)}
        />
      </MainLayout>
    </PageviewTracker>
  );
};
